/* eslint-disable react/no-array-index-key */
/* eslint-disable no-return-assign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import memoize from 'fast-memoize';
import { withDeviceInfo } from 'ggApp/shared/components/viewport';
import { addBodyClass, removeBodyClass } from 'ggApp/modules/layout/actions';
import throttle from 'ggApp/utils/throttle';
import ZoomedGallery from './ZoomedGallery';

import {
    Root,
    SlideImage,
    Slider,
    SliderWrap,
    ThumbImg,
    ThumbList,
    ThumbWrap,
    ZoomButton,
} from './styled';

const eventForceOptions = {
    capture: false,
    passive: false,
};

const isIosDevice = () => {
    let iOS;
    if (typeof window !== 'undefined' && typeof window.navigator !== 'undefined') {
        const ua = window.navigator.userAgent;
        iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    }
    return iOS;
};

@withDeviceInfo
export default class ImageGallery extends Component {
    slidesRefs = [];

    slidesAspectRatio = [];

    thumbRefList = [];

    defaultState = {
        index: 0,
        isOpen: false,
        openable: false,
        slidesAspectRatio: [],
    };

    onThumbnailHover = memoize((newIndex) => () => {
        const { index } = this.state;
        if (newIndex !== index) {
            this.setState({ index: newIndex });
        }
    });

    onThumbnailClick = memoize((newIndex) => () => {
        if (this.thumbRefList[newIndex || 0]) {
            this.thumbRefList.forEach((node) => node && node.classList.remove('active'));
            this.thumbRefList[newIndex || 0].classList.add('active');
            this.sliderRef.scrollLeft = window.innerWidth * newIndex;
        }
    });

    saveSlidesAspectRatio = memoize((idx) => (e) => {
        const { imageList } = this.props;
        const imageAR = e.target.naturalWidth / e.target.naturalHeight;
        const { src, srcset, sizes } = e.target;
        this.slidesAspectRatio[idx] = {
            imageAR,
            src,
            srcSet: srcset,
            sizes,
        };
        if (Object.keys(this.slidesAspectRatio).length === imageList.length) {
            this.setState({
                slidesAspectRatio: this.slidesAspectRatio,
                openable: true,
            });
        }
    });

    toggleZoomedGalleryWithIndex = memoize((index) => () => {
        this.setState({ index }, this.toggleZoomedGallery);
    });

    constructor(props) {
        super(props);
        this.throttledSliderOnScroll = throttle(this.setActiveIndex, 200);
        this.state = this.defaultState;
    }

    componentWillReceiveProps(nextProps) {
        const { imageList } = this.props;
        const { imageList: nextImageList } = nextProps;
        if (imageList !== nextImageList) {
            this.slidesAspectRatio = [];
            this.setState({ ...this.defaultState });
        }
    }

    componentWillUnmount() {
        this.enableScroll();
    }

    openZoomGallery = (idx) => () => {
        const { openable } = this.state;
        if (openable) {
            if (!isIosDevice() || window.innerWidth === document.body.getClientRects()[0].width) {
                this.toggleZoomedGalleryWithIndex(idx)();
            }
        }
    };

    // eslint-disable-next-line react/destructuring-assignment
    isMobile = () => this.props.userDevice === 'mobile';

    preventScrollIfNeeded = (e) => {
        const { clientX: newX, clientY: newY } = e.touches[0];
        if (Math.abs(this.lastX - newX) > Math.abs(this.lastY - newY)) {
            e.stopPropagation();
            return true;
        }
        e.preventDefault();
        return false;
    };

    preventScrollSetUp = (e) => {
        if (e && e.touches && e.touches[0]) {
            this.lastX = e.touches[0].clientX;
            this.lastY = e.touches[0].clientY;
        }
    };

    disableScroll = () => {
        addBodyClass('disableScroll');
        window.addEventListener('touchmove', this.preventScrollIfNeeded, eventForceOptions);
        window.addEventListener('touchstart', this.preventScrollSetUp, eventForceOptions);
    };

    enableScroll = () => {
        removeBodyClass('disableScroll');
        window.removeEventListener('touchmove', this.preventScrollIfNeeded, eventForceOptions);
        window.removeEventListener('touchstart', this.preventScrollSetUp, eventForceOptions);
    };

    toggleZoomedGallery = () => {
        const { isOpen } = this.state;
        if (!isOpen) {
            this.openZoomedGallery();
        }
        if (isOpen) {
            this.closeZoomedGallery();
        }
    };

    openZoomedGallery = () => {
        this.disableScroll();
        this.setState({
            isOpen: true,
        });
    };

    closeZoomedGallery = () => {
        this.enableScroll();
        this.setState({
            isOpen: false,
        });
    };

    setIndex = (newIndex, callback) => {
        const { index } = this.state;
        if (newIndex !== index) {
            this.setState({ index: newIndex }, callback);
        }
    };

    setActiveIndex = (e) => {
        const thumbnailsTotal = this.thumbRefList?.length;
        const thumbnailsWidth = e.target.scrollWidth / thumbnailsTotal;
        const newIndex = Math.round(e.target.scrollLeft / thumbnailsWidth);
        if (this.thumbRefList[newIndex || 0]) {
            this.thumbRefList.forEach((node) => node && node.classList.remove('active'));
            this.thumbRefList[newIndex || 0].classList.add('active');
        }
    };

    render() {
        const { className, imageList, thumbnailStyle } = this.props;
        const { index, isOpen, openable, slidesAspectRatio } = this.state;

        if (imageList.length === 0) return null;

        return (
            <div className={className}>
                <Root isOpen={isOpen} ref={(el) => (this.rootRef = el)}>
                    {imageList.length !== 1 && (
                        <ThumbList>
                            {imageList.map((_, idx) => {
                                const imgSrc =
                                    imageList[idx] &&
                                    imageList[idx].urls &&
                                    imageList[idx].urls.mini_square;
                                return (
                                    <ThumbWrap
                                        key={idx}
                                        ref={(el) => (this.thumbRefList[idx] = el)}
                                        onMouseMove={this.onThumbnailHover(idx)}
                                        onClick={this.onThumbnailClick(idx)}
                                        className={this.isMobile() && idx === index && 'active'}
                                        active={!this.isMobile() && idx === index}
                                        thumbnailStyle={thumbnailStyle}
                                    >
                                        <ThumbImg src={imgSrc} imgUrls={imgSrc} />
                                    </ThumbWrap>
                                );
                            })}
                        </ThumbList>
                    )}
                    <SliderWrap>
                        <Slider
                            ref={(el) => (this.sliderRef = el)}
                            onScroll={this.throttledSliderOnScroll}
                        >
                            {imageList.map((slide, idx) => (
                                <SlideImage
                                    key={idx}
                                    draggable="false"
                                    refFunc={(el) => (this.slidesRefs[idx] = el)}
                                    sizes={{
                                        desktop: '60vw',
                                        default: '100vw',
                                    }}
                                    visible={this.isMobile() || index === idx}
                                    onClick={this.openZoomGallery(idx)}
                                    onLoad={this.saveSlidesAspectRatio(idx)}
                                    imgUrls={slide.urls}
                                    src={slide.urls && slide.urls.medium_square}
                                    lazy={false}
                                    alt={slide.alt}
                                />
                            ))}
                            {openable && <ZoomButton onClick={this.toggleZoomedGallery} />}
                        </Slider>
                    </SliderWrap>
                </Root>
                {openable && (
                    <ZoomedGallery
                        rootRef={this.rootRef}
                        parentSliderRef={this.sliderRef}
                        index={index}
                        isOpen={isOpen}
                        close={this.closeZoomedGallery}
                        slidesAspectRatio={slidesAspectRatio}
                        setParentActiveIndex={this.setIndex}
                        {...this.props}
                    />
                )}
            </div>
        );
    }
}

ImageGallery.propTypes = {
    className: PropTypes.string,
    imageList: PropTypes.arrayOf(
        PropTypes.shape({
            big: PropTypes.string,
            mini: PropTypes.string,
        }).isRequired,
    ),
    thumbnailStyle: PropTypes.string,
};

ImageGallery.defaultProps = {
    className: '',
    thumbnailStyle: '',
    imageList: [],
};
